
import React, { useRef, useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Send, Camera, Paperclip, X } from "lucide-react";
import { EmojiPicker } from "./EmojiPicker";
import { CameraCapture } from "./CameraCapture";

interface ChatInputProps {
  newMessage: string;
  setNewMessage: (message: string) => void;
  sendMessage: () => void;
  showEmojiPicker: boolean;
  setShowEmojiPicker: (show: boolean) => void;
  addEmoji: (emoji: string) => void;
  editingMessage: string | null;
  editText: string;
  setEditText: (text: string) => void;
  cancelEdit: () => void;
  saveEdit: (messageId: string | null) => void;
  selectedFile: File | null;
  filePreview: string | null;
  onFileSelect: (file: File) => void;
  onFileClear: () => void;
  uploading?: boolean;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  newMessage,
  setNewMessage,
  sendMessage,
  showEmojiPicker,
  setShowEmojiPicker,
  addEmoji,
  editingMessage,
  editText,
  setEditText,
  cancelEdit,
  saveEdit,
  selectedFile,
  filePreview,
  onFileSelect,
  onFileClear,
  uploading = false
}) => {
  const [showCamera, setShowCamera] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onFileSelect(file);
    }
    // Reset input so the same file can be selected again
    e.target.value = "";
  };

  const canSend = newMessage.trim() || selectedFile;

  return (
    <div className="p-4 border-t">
      {/* Camera modal */}
      <CameraCapture
        open={showCamera}
        onOpenChange={setShowCamera}
        onCapture={(file) => {
          onFileSelect(file);
          setShowCamera(false);
        }}
      />
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        onChange={handleFileChange}
      />

      {editingMessage ? (
        // Editing mode
        <div className="space-y-2">
          <div className="text-sm text-muted-foreground mb-1">Bericht bewerken</div>
          <Textarea 
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            className="min-h-[60px] resize-none"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                saveEdit(editingMessage);
              }
            }}
          />
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={cancelEdit}>
              Annuleren
            </Button>
            <Button 
              onClick={() => saveEdit(editingMessage)}
              disabled={!editText.trim()}
            >
              Opslaan
            </Button>
          </div>
        </div>
      ) : (
        // Normal message mode
        <div className="space-y-2">
          {/* File preview */}
          {filePreview && selectedFile && (
            <div className="relative inline-block">
              {selectedFile.type.startsWith("image/") ? (
                <img
                  src={filePreview}
                  alt="Preview"
                  className="h-20 w-20 object-cover rounded-lg border"
                />
              ) : (
                <div className="h-20 w-20 rounded-lg border bg-muted flex items-center justify-center text-xs text-muted-foreground text-center p-1">
                  {selectedFile.name}
                </div>
              )}
              <Button
                variant="destructive"
                size="icon"
                className="absolute -top-2 -right-2 h-5 w-5 rounded-full"
                onClick={onFileClear}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          )}

          <div className="flex gap-2">
            <div className="flex sm:flex-col flex-row gap-1 self-end">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setShowCamera(true)}
                title="Camera"
              >
                <Camera className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => fileInputRef.current?.click()}
                title="Bestand uploaden"
              >
                <Paperclip className="h-4 w-4" />
              </Button>
            </div>
            <Textarea 
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Typ een bericht..."
              className="min-h-[60px] resize-none"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
            />
            <div className="flex flex-col gap-2 self-end">
              <EmojiPicker 
                showEmojiPicker={showEmojiPicker}
                setShowEmojiPicker={setShowEmojiPicker}
                addEmoji={addEmoji}
              />
              <Button 
                onClick={sendMessage}
                disabled={!canSend || uploading}
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
